import { z } from 'zod';

import { IndustryRefSchema, OccupationRefSchema } from '@/modules/shared';

import {
	EducationLevelEnum,
	EmploymentStatusEnum,
	GenderEnum,
	GraduationStatusEnum,
	JobChangeCountEnum,
} from '@packages/contracts';

// ----------------------------------------------------------------------
export const PersonalInfoStepSchema = z.object({
	email:     z.string().email('Invalid email'),
	firstName: z.string().min(1, 'First name is required'),
	lastName:  z.string().min(1, 'Last name is required'),
	password:  z.string().min(6, 'Password must be at least 6 characters long'),
});
export type PersonalInfoStep = z.infer<typeof PersonalInfoStepSchema>;

// ----------------------------------------------------------------------
export const DateOfBirthStepSchema = z.object({ dateOfBirth: z.string().min(1, 'Date of birth is required') });
export type DateOfBirthStep = z.infer<typeof DateOfBirthStepSchema>;

// ----------------------------------------------------------------------
const GenderEnumSchema = z.enum(GenderEnum.options, { message: 'Gender is required' });
export const GenderStepSchema = z.object({ gender: GenderEnumSchema });
export type GenderStep = z.infer<typeof GenderStepSchema>;

// ----------------------------------------------------------------------
export const PhoneNumberStepSchema = z.object({ phoneNumber: z.string().min(9, 'Phone number is required') });
export type PhoneNumberStep = z.infer<typeof PhoneNumberStepSchema>;

// ----------------------------------------------------------------------
export const ResidenceStepSchema = z.object({ provinceId: z.string().min(1, 'Residence area is required') });
export type ResidenceStep = z.infer<typeof ResidenceStepSchema>;

// ----------------------------------------------------------------------
const EducationEnumSchema = z.enum(EducationLevelEnum.options, { message: 'Education level is required' });
export const EducationStepSchema = z.object({ educationLevel: EducationEnumSchema });
export type EducationStep = z.infer<typeof EducationStepSchema>;

// ----------------------------------------------------------------------
const GraduationStatusEnumSchema = z.enum(GraduationStatusEnum.options, { message: 'Graduation status is required' });
export const GraduationStatusStepSchema = z.object({
	graduationDate:   z.string().optional(),
	graduationStatus: GraduationStatusEnumSchema,
}).refine((data) => {
	const isDropOut = data.graduationStatus === GraduationStatusEnumSchema.enum.PENDING;

	return isDropOut ? true : !!data.graduationDate;
}, {
	message: 'Graduation date is required',
	path:    [ 'graduationDate' ],
});
export type GraduationStatusStep = z.infer<typeof GraduationStatusStepSchema>;

// ----------------------------------------------------------------------
const EmploymentStatusEnumSchema = z.enum(EmploymentStatusEnum.options, { message: 'Employment status is required' });
export const EmploymentStatusStepSchema = z.object({ employmentStatus: EmploymentStatusEnumSchema });
export type EmploymentStatusStep = z.infer<typeof EmploymentStatusStepSchema>;

// ----------------------------------------------------------------------
const JobChangeCountEnumSchema = z.enum(JobChangeCountEnum.options, { message: 'Job change count is required' });
export const JobChangedCountStepSchema = z.object({ jobChangeCount: JobChangeCountEnumSchema });
export type JobChangedCountStep = z.infer<typeof JobChangedCountStepSchema>;

// ----------------------------------------------------------------------
export const CurrentCompanyStepSchema = z.object({
	companyName:     z.string().min(1, 'Company name is required'),
	hideCompanyName: z.boolean().default(true),
});
export type CurrentCompanyStep = z.infer<typeof CurrentCompanyStepSchema>;

// ----------------------------------------------------------------------
export const JobIndustryStepSchema = IndustryRefSchema;
export type JobIndustryStep = z.infer<typeof JobIndustryStepSchema>;

// ----------------------------------------------------------------------
export const JobOccupationStepSchema = OccupationRefSchema;
export type JobOccupationStep = z.infer<typeof JobOccupationStepSchema>;

// ----------------------------------------------------------------------
export const AnnualSalaryStepSchema = z.object({
	currentAnnualSalary:  z.string(),
	expectedAnnualSalary: z.string(),
});
export type AnnualSalaryStep = z.infer<typeof AnnualSalaryStepSchema>;

// ----------------------------------------------------------------------
export const DesiredJobOccupationStepSchema = z.object({ preferredJobOccupations: OccupationRefSchema.array().max(3) });
export type DesiredJobOccupationStep = z.infer<typeof DesiredJobOccupationStepSchema>;

// ----------------------------------------------------------------------
export const DesiredJobLocationStepSchema = z.object({
	openToAnyLocations:   z.boolean().optional(),
	preferredLocationIds: z.string().array().max(3),
});
export type DesiredJobLocationStep = z.infer<typeof DesiredJobLocationStepSchema>;
